/**
 * useSignedMediaUrl — Shared hook for resolving storage paths to signed URLs
 *
 * Used by ALL media components (V1 + V2) to resolve storage paths to signed URLs.
 * Handles: cache (50 min TTL), fresh URL on demand, external URL pass-through, error states.
 *
 * FAILURE BEHAVIOR: Returns signedUrl=null on error (NOT the raw path).
 * Raw paths would be rejected by V2's isAllowedMediaUrl() → "Media unavailable".
 * Returning null lets components show their standard error/fallback state.
 *
 * Components that DON'T need this hook:
 * - MediaGif.tsx / MediaSticker.tsx — use downloadAndGetMediaUrl() which goes through
 *   whatsappMediaService (updated separately). No hook needed.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import {
  TENANT_BUCKET,
  SIGNED_URL_TTL,
  FRONTEND_CACHE_TTL_MS,
  FRONTEND_CACHE_MAX_ENTRIES,
  FRONTEND_CACHE_EVICT_COUNT,
  extractStoragePath,
} from '@/lib/storage-config';

// ============================================================================
// LRU CACHE (singleton, shared across all hook instances)
// ============================================================================

interface CacheEntry {
  url: string;
  expiresAt: number; // Date.now() + TTL
  insertedAt: number; // for LRU eviction
}

const cache = new Map<string, CacheEntry>();

/**
 * How long a signing request may take before this hook answers "could not" instead of staying
 * silent. There was no bound at all until 2026-09-08: a request that hung left `loading` true for
 * the lifetime of the component, so whoever rendered it showed a placeholder that never resolved.
 * It is the same shape as the permanent grey block found under "Channels" in the sidebar the same
 * day — a loading state with no upper bound is a check that can only say yes.
 */
const SIGN_TIMEOUT_MS = 15_000;

function getCached(key: string): string | null {
  const entry = cache.get(key);
  if (!entry) return null;
  if (Date.now() >= entry.expiresAt) {
    cache.delete(key);
    return null;
  }
  // Update access time for LRU
  entry.insertedAt = Date.now();
  return entry.url;
}

function setCache(key: string, url: string): void {
  // LRU eviction: remove oldest entries when at capacity
  if (cache.size >= FRONTEND_CACHE_MAX_ENTRIES) {
    const entries = [...cache.entries()]
      .sort((a, b) => a[1].insertedAt - b[1].insertedAt);
    for (let i = 0; i < FRONTEND_CACHE_EVICT_COUNT && i < entries.length; i++) {
      cache.delete(entries[i][0]);
    }
  }

  cache.set(key, {
    url,
    expiresAt: Date.now() + FRONTEND_CACHE_TTL_MS,
    insertedAt: Date.now(),
  });
}

// ============================================================================
// SIGNING
// ============================================================================

/** Resolve relative path to full storage path by prepending tenant ID */
async function resolveFullPath(path: string): Promise<string> {
  // Already has tenant ID prefix ({tid}/...) — use as-is
  if (/^[0-9a-f]{8}-/.test(path)) return path;

  // Relative path — need tenant ID from auth
  const { data: { user } } = await supabase.auth.getUser();
  const tenantId = user?.user_metadata?.tenant_id;
  if (!tenantId) return path; // fallback — can't resolve without tenant ID

  // _platform/ paths just need tenant ID prefix
  if (path.startsWith('_platform/')) return `${tenantId}/${path}`;

  // custom-storage paths
  return `${tenantId}/custom-storage/${path}`;
}

async function signPath(path: string): Promise<string | null> {
  const fullPath = await resolveFullPath(path);

  const { data, error } = await supabase.storage
    .from(TENANT_BUCKET)
    .createSignedUrl(fullPath, SIGNED_URL_TTL.FRONTEND);

  if (error || !data?.signedUrl) {
    if (import.meta.env.DEV) console.warn('[useSignedMediaUrl] Sign failed:', fullPath, error?.message);
    return null; // MUST return null, NOT the raw path
  }

  return data.signedUrl;
}

// ============================================================================
// BATCH RESOLVE (for message list pre-population)
// ============================================================================

/**
 * Batch-resolve multiple media paths to signed URLs.
 * Call from message list level to pre-populate cache before individual hooks fire.
 * External URLs pass through unchanged.
 */
export async function batchResolveMediaUrls(paths: (string | null | undefined)[]): Promise<void> {
  const toSign: { key: string; fullPath: string }[] = [];

  for (const urlOrPath of paths) {
    if (!urlOrPath) continue;
    const extracted = extractStoragePath(urlOrPath);
    if (extracted === null) continue; // external URL
    const cached = getCached(extracted);
    if (cached) continue; // already in cache

    toSign.push({ key: extracted, fullPath: '' }); // fullPath resolved below
  }

  if (toSign.length === 0) return;

  // Resolve full paths (prepend tenant ID for relative paths)
  for (const entry of toSign) {
    entry.fullPath = await resolveFullPath(entry.key);
  }

  // Chunk at 100
  const CHUNK_SIZE = 100;
  for (let i = 0; i < toSign.length; i += CHUNK_SIZE) {
    const chunk = toSign.slice(i, i + CHUNK_SIZE);
    const pathsToSign = chunk.map(e => e.fullPath);

    const { data, error } = await supabase.storage
      .from(TENANT_BUCKET)
      .createSignedUrls(pathsToSign, SIGNED_URL_TTL.FRONTEND);

    if (error || !data) {
      if (import.meta.env.DEV) console.warn('[batchResolveMediaUrls] Batch sign failed:', error?.message);
      continue;
    }

    for (let j = 0; j < chunk.length; j++) {
      const signed = data[j];
      if (signed?.signedUrl) {
        setCache(chunk[j].key, signed.signedUrl);
      }
    }
  }
}

// ============================================================================
// HOOK
// ============================================================================

interface UseSignedMediaUrlResult {
  signedUrl: string | null;
  loading: boolean;
  error: string | null;
  /** Generate a fresh signed URL (bypasses cache). Use for zoom/download actions. */
  refreshUrl: () => Promise<string | null>;
}

export function useSignedMediaUrl(urlOrPath: string | null | undefined): UseSignedMediaUrlResult {
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Resolve on mount and when urlOrPath changes
  useEffect(() => {
    if (!urlOrPath) {
      setSignedUrl(null);
      setLoading(false);
      setError(null);
      return;
    }

    const extracted = extractStoragePath(urlOrPath);

    // External URL — pass through immediately
    if (extracted === null) {
      setSignedUrl(urlOrPath);
      setLoading(false);
      setError(null);
      return;
    }

    // Check cache first
    const cached = getCached(extracted);
    if (cached) {
      setSignedUrl(cached);
      setLoading(false);
      setError(null);
      return;
    }

    // Sign async
    setLoading(true);
    setError(null);

    // BOUNDED, AND THAT IS THE POINT. This used to be a bare `.then` with no timeout and no
    // `.catch`: a signing request that hung, or one that rejected instead of resolving null, left
    // `loading` true forever, and whoever rendered this hook showed a placeholder that never
    // resolved. A customer's photo that pretends to still be arriving is worse than one that says
    // it could not be fetched, because the operator waits instead of asking for it again.
    let settled = false;
    const finish = (url: string | null, failure: string | null) => {
      if (settled || !mountedRef.current) return;
      settled = true;
      if (url) {
        setCache(extracted, url);
        setSignedUrl(url);
        setError(null);
      } else {
        setSignedUrl(null); // null, NOT raw path
        setError(failure ?? 'Failed to sign media URL');
      }
      setLoading(false);
    };

    const watchdog = setTimeout(() => finish(null, 'Timed out signing media URL'), SIGN_TIMEOUT_MS);

    signPath(extracted)
      .then(url => finish(url, null))
      .catch(() => finish(null, 'Failed to sign media URL'))
      .finally(() => clearTimeout(watchdog));

    return () => clearTimeout(watchdog);
  }, [urlOrPath]);

  // Refresh: always generate fresh (for zoom/download after long sessions)
  const refreshUrl = useCallback(async (): Promise<string | null> => {
    if (!urlOrPath) return null;

    const extracted = extractStoragePath(urlOrPath);
    if (extracted === null) return urlOrPath; // external URL

    const url = await signPath(extracted);
    if (url && mountedRef.current) {
      setCache(extracted, url);
      setSignedUrl(url);
    }
    return url;
  }, [urlOrPath]);

  return { signedUrl, loading, error, refreshUrl };
}
